<template>
    <VJoyDialog ref="productTourDialog" data-testid="profile-revamp-onboarding-dialog" :open="shouldShowDialog" @dialog:hide="closeAndAcknowledge">
        <template #dialog-body>
            <div class="profile-product-tour__image-container">
                <MaltImg :src="dialogConfig.imageSrc" alt="Profile product tour image" />
            </div>
        </template>
        <template #dialog-header>
            {{ dialogConfig.headerTitle }}
        </template>
        <template #dialog-subheader>
            {{ dialogConfig.subheaderTitle }}
        </template>
        <template #dialog-cancel>
            <VJoyButton v-if="!dialogConfig.hideCancelButton" variant="ghost" :label="dialogConfig.cancelText" @click="closeAndAcknowledge" />
        </template>
        <template #dialog-confirm>
            <VJoyButton variant="main" icon="arrow-right" icon-position="right" @click="dialogConfig.confirm">
                {{ dialogConfig.confirmText }}
            </VJoyButton>
        </template>
    </VJoyDialog>
    <ProfileProductTour v-if="productTourOpen" />
</template>

<script lang="ts" setup>
    import {ref, computed, useTemplateRef} from 'vue';
    import {storeToRefs} from 'pinia';
    import {MaltImg} from '#components';
    import {useAuth, useProfilePageViewMode, useTranslation} from '#imports';
    import {VJoyDialog, VJoyButton} from '@maltjoy/core-vue';
    import ProfileProductTour from '~/features/product-tour/ui/ProfileProductTour.vue';
    import {useWebPreferencesStore} from '~/store/webPreferences.store';

    const {t} = useTranslation();

    const productTourDialog = useTemplateRef<InstanceType<typeof VJoyDialog>>('productTourDialog');
    const productTourOpen = ref(false);
    const webPreferencesStore = useWebPreferencesStore();
    const {webPreferences} = storeToRefs(webPreferencesStore);
    const {isEditionEnabled} = useProfilePageViewMode();
    const {currentIdentityIsClient} = storeToRefs(useAuth());

    const shouldShowDialog = computed(() => {
        return (currentIdentityIsClient.value || isEditionEnabled.value) && !webPreferences.value?.productTourAcknowledged;
    });

    const dialogConfig = computed(() =>
        currentIdentityIsClient.value
            ? {
                  imageSrc: '/ClientProfileProductTour.svg',
                  headerTitle: t('profile.product.tour.client.dialog.header.title'),
                  subheaderTitle: t('profile.product.tour.dialog.subheader.title'),
                  confirmText: t('profile.product.tour.dialog.cancel.text'),
                  hideCancelButton: true,
                  confirm: closeAndAcknowledge,
              }
            : {
                  imageSrc: '/ProfileProductTour.svg',
                  headerTitle: t('profile.product.tour.dialog.header.title'),
                  subheaderTitle: t('profile.product.tour.dialog.subheader.title'),
                  cancelText: t('profile.product.tour.dialog.cancel.text'),
                  confirmText: t('profile.product.tour.dialog.confirm.text'),
                  confirm: showProductTour,
              },
    );

    async function closeAndAcknowledge() {
        await productTourDialog.value?.hide({fireHideEvent: false});
        await webPreferencesStore.acknowledgeProfileProductTour();
    }

    async function showProductTour() {
        await productTourDialog.value?.hide();
        productTourOpen.value = true;
    }
</script>
<style lang="scss">
    .profile-product-tour__image-container {
        display: grid;
        place-items: center;

        img {
            height: 40vh;
            max-height: 390px;
            border-radius: var(--joy-core-radius-4);
        }
    }
</style>
